/**
 * Style injection utility - idempotent CSS injection into document head
 * @module lib/utils/styleInjector
 */

/**
 * Inject CSS styles into the document head (idempotent)
 * @param {string} id - Unique ID for the style element (prevents duplicates)
 * @param {string} cssContent - The CSS content to inject
 * @returns {boolean} Whether styles were injected (false if already exists or unavailable)
 */
function injectStyles(id, cssContent, target) {
  if (typeof document === 'undefined') {
    return false;
  }
  const root = target || document;
  const owner = root.ownerDocument || root;
  if (root.querySelector(`#${id}`)) {
    return false;
  }

  const style = owner.createElement('style');
  style.id = id;
  style.type = 'text/css';
  style.textContent = cssContent;

  if (root.head || root.host) {
    (root.head || root).appendChild(style);
    return true;
  }

  return false;
}

/**
 * Create an idempotent style injector function for specific styles
 * @param {string} id - Unique ID for the style element
 * @param {string} cssContent - The CSS content to inject
 * @returns {function} A function that injects the styles when called (idempotent)
 */
function createStyleInjector(id, cssContent) {
  return function ensureStyles() {
    return injectStyles(id, cssContent);
  };
}

module.exports = {
  injectStyles,
  createStyleInjector
};
